// Day/night determination for flight logs. Pure maths, no dependencies —
// shared between the API (tests) and the app (auto-filling Flight
// Conditions from the UTC clocks + airport positions).
//
// "Night" follows the aviation (EASA/ICAO) definition: the sun's centre more
// than 6° below the horizon, i.e. between the end of evening civil twilight
// and the start of morning civil twilight.

export type LatLon = { lat: number; lon: number }
export type DayNightPhase = 'Day' | 'Night'

const DEG = Math.PI / 180
const CIVIL_TWILIGHT_ALTITUDE_DEG = -6

// Sun altitude above the horizon (degrees) at a UTC instant and location.
// NOAA solar-position algorithm (Meeus), accurate to well under a degree —
// plenty for a day/night split.
export function sunAltitudeDeg(date: Date, latDeg: number, lonDeg: number): number {
  const jd = date.getTime() / 86400000 + 2440587.5
  const t = (jd - 2451545) / 36525 // Julian centuries from J2000

  const meanLon = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360
  const meanAnom = 357.52911 + t * (35999.05029 - 0.0001537 * t)
  const ecc = 0.016708634 - t * (0.000042037 + 0.0000001267 * t)
  const m = meanAnom * DEG
  const centre =
    Math.sin(m) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * m) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * m) * 0.000289
  const trueLon = meanLon + centre
  const omega = 125.04 - 1934.136 * t
  const apparentLon = trueLon - 0.00569 - 0.00478 * Math.sin(omega * DEG)

  const obliq0 = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60
  const obliq = obliq0 + 0.00256 * Math.cos(omega * DEG)

  const declination = Math.asin(Math.sin(obliq * DEG) * Math.sin(apparentLon * DEG))

  const y = Math.tan((obliq / 2) * DEG) ** 2
  const eqTimeMin =
    4 *
    (180 / Math.PI) *
    (y * Math.sin(2 * meanLon * DEG) -
      2 * ecc * Math.sin(m) +
      4 * ecc * y * Math.sin(m) * Math.cos(2 * meanLon * DEG) -
      0.5 * y * y * Math.sin(4 * meanLon * DEG) -
      1.25 * ecc * ecc * Math.sin(2 * m))

  const minutesUtc =
    date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60
  const trueSolarMin = (minutesUtc + eqTimeMin + 4 * lonDeg + 1440) % 1440
  const hourAngleDeg = trueSolarMin / 4 - 180

  const lat = latDeg * DEG
  const cosZenith =
    Math.sin(lat) * Math.sin(declination) +
    Math.cos(lat) * Math.cos(declination) * Math.cos(hourAngleDeg * DEG)
  return 90 - Math.acos(Math.max(-1, Math.min(1, cosZenith))) / DEG
}

export function isAviationNight(date: Date, lat: number, lon: number): boolean {
  return sunAltitudeDeg(date, lat, lon) < CIVIL_TWILIGHT_ALTITUDE_DEG
}

// Position `fraction` (0..1) of the way along the great circle from → to.
export function interpolateGreatCircle(from: LatLon, to: LatLon, fraction: number): LatLon {
  const toVec = ({ lat, lon }: LatLon) => {
    const φ = lat * DEG
    const λ = lon * DEG
    return [Math.cos(φ) * Math.cos(λ), Math.cos(φ) * Math.sin(λ), Math.sin(φ)] as const
  }
  const a = toVec(from)
  const b = toVec(to)
  const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]))
  const angle = Math.acos(dot)
  if (angle < 1e-9) return from
  const wa = Math.sin((1 - fraction) * angle) / Math.sin(angle)
  const wb = Math.sin(fraction * angle) / Math.sin(angle)
  const x = wa * a[0] + wb * b[0]
  const y = wa * a[1] + wb * b[1]
  const z = wa * a[2] + wb * b[2]
  return { lat: Math.atan2(z, Math.hypot(x, y)) / DEG, lon: Math.atan2(y, x) / DEG }
}

export type DayNightSplit = {
  takeOffPhase: DayNightPhase
  landingPhase: DayNightPhase
  dayMinutes: number
  nightMinutes: number
}

// Walk the flight minute by minute along the great-circle track and count
// how much of it was flown in day vs night.
export function computeDayNight(args: {
  takeOff: Date
  landing: Date
  from: LatLon
  to: LatLon
}): DayNightSplit {
  const { takeOff, landing, from, to } = args
  const totalMinutes = Math.max(0, Math.round((landing.getTime() - takeOff.getTime()) / 60000))
  const phaseAt = (date: Date, pos: LatLon): DayNightPhase =>
    isAviationNight(date, pos.lat, pos.lon) ? 'Night' : 'Day'

  let nightMinutes = 0
  for (let i = 0; i < totalMinutes; i++) {
    // Sample the midpoint of each minute
    const fraction = (i + 0.5) / totalMinutes
    const at = new Date(takeOff.getTime() + (i + 0.5) * 60000)
    const pos = interpolateGreatCircle(from, to, fraction)
    if (isAviationNight(at, pos.lat, pos.lon)) nightMinutes += 1
  }

  return {
    takeOffPhase: phaseAt(takeOff, from),
    landingPhase: phaseAt(landing, to),
    dayMinutes: totalMinutes - nightMinutes,
    nightMinutes,
  }
}

// Turn the log's UTC "HH:mm" take-off clock into an instant. The clock is
// anchored on the scheduled departure and snapped to the nearest instance
// (±12 h) so a flight logged just after midnight UTC lands on the right day.
export function resolveLegClocks(args: {
  departureDateTime: Date
  takeOff: string // "HH:mm" UTC
  flightMinutes: number
}): { takeOff: Date; landing: Date } | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(args.takeOff)
  if (!match || Number.isNaN(args.departureDateTime.getTime())) return null
  const [, hh, mm] = match
  const anchor = args.departureDateTime
  const candidate = new Date(
    Date.UTC(
      anchor.getUTCFullYear(),
      anchor.getUTCMonth(),
      anchor.getUTCDate(),
      Number(hh),
      Number(mm)
    )
  )
  const halfDay = 12 * 60 * 60000
  let takeOff = candidate
  if (candidate.getTime() - anchor.getTime() > halfDay) {
    takeOff = new Date(candidate.getTime() - 24 * 60 * 60000)
  } else if (anchor.getTime() - candidate.getTime() > halfDay) {
    takeOff = new Date(candidate.getTime() + 24 * 60 * 60000)
  }
  const landing = new Date(takeOff.getTime() + Math.max(0, args.flightMinutes) * 60000)
  return { takeOff, landing }
}
